<script>
  import { createEventDispatcher } from 'svelte'
  import ExerciseSelector from './ExerciseSelector.svelte'
  import { createDefaultSet } from '../services/db.js'

  export let exercise = {
    id: '',
    exercise_name: '',
    sets: [createDefaultSet()],
    rest_time: '',
    notes: ''
  }
  export let index = 0

  const dispatch = createEventDispatcher()

  function handleRemove() {
    dispatch('remove', { id: exercise.id })
  }

  function handleSelect(event) {
    exercise.exercise_name = event.detail.name
  }

  function addSet() {
    const lastSet = exercise.sets[exercise.sets.length - 1]
    exercise.sets = [...exercise.sets, createDefaultSet(lastSet)]
  }

  function removeSet(setIndex) {
    if (exercise.sets.length <= 1) return
    exercise.sets = exercise.sets.filter((_, i) => i !== setIndex)
  }

  function toggleCompleted(setIndex) {
    exercise.sets[setIndex].completed = !exercise.sets[setIndex].completed
    exercise.sets = exercise.sets
  }
</script>

<div class="exercise-input-card bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
  <div class="flex justify-between items-start mb-3">
    <h3 class="text-lg font-semibold text-gray-700">運動 {index + 1}</h3>
    <button
      type="button"
      class="remove-exercise text-red-600 hover:text-red-800 font-medium"
      on:click={handleRemove}
    >
      移除
    </button>
  </div>

  <div class="space-y-3">
    <!-- Exercise Name (Selector) -->
    <div>
      <label class="block text-sm font-medium text-gray-700 mb-1">
        運動名稱 <span class="text-red-500">*</span>
      </label>
      <ExerciseSelector
        selectedName={exercise.exercise_name}
        on:select={handleSelect}
      />
    </div>

    <!-- Rest Time -->
    <div>
      <label class="block text-sm font-medium text-gray-700 mb-1">
        組間休息時間
      </label>
      <div class="flex items-center gap-2">
        <input
          type="number"
          class="input-field flex-1"
          placeholder="90"
          bind:value={exercise.rest_time}
          min="0"
          max="3600"
        />
        <span class="text-sm text-gray-500">秒</span>
      </div>
      <div class="flex gap-2 mt-2">
        <button
          type="button"
          class="text-xs px-2 py-1 rounded bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors"
          on:click={() => exercise.rest_time = 60}
        >
          60s
        </button>
        <button
          type="button"
          class="text-xs px-2 py-1 rounded bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors"
          on:click={() => exercise.rest_time = 90}
        >
          90s
        </button>
        <button
          type="button"
          class="text-xs px-2 py-1 rounded bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors"
          on:click={() => exercise.rest_time = 120}
        >
          120s
        </button>
        <button
          type="button"
          class="text-xs px-2 py-1 rounded bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors"
          on:click={() => exercise.rest_time = 180}
        >
          180s
        </button>
      </div>
    </div>

    <!-- Notes -->
    <div>
      <label class="block text-sm font-medium text-gray-700 mb-1">
        運動備註
      </label>
      <textarea
        class="input-field w-full text-sm"
        rows="2"
        placeholder="例如：注意姿勢、提醒事項等..."
        maxlength="200"
        bind:value={exercise.notes}
      ></textarea>
    </div>

    <!-- Sets Table -->
    <div class="mt-4">
      <div class="flex justify-between items-center mb-2">
        <span class="text-sm font-medium text-gray-700">組數明細</span>
        <span class="text-xs text-gray-500">共 {exercise.sets.length} 組</span>
      </div>
      <div class="grid grid-cols-12 gap-2 text-xs font-medium text-gray-500 mb-2 px-1">
        <div class="col-span-2">SET</div>
        <div class="col-span-4">KG</div>
        <div class="col-span-4">REPS</div>
        <div class="col-span-2 text-center"></div>
      </div>

      <div class="space-y-2">
        {#each exercise.sets as set, setIndex}
          <div
            class="grid grid-cols-12 gap-2 items-center p-2 rounded-lg transition-all duration-200"
            class:bg-green-50={set.completed}
            class:border-l-4={set.completed}
            class:border-green-500={set.completed}
            class:bg-gray-50={!set.completed}
          >
            <div class="col-span-2 text-sm font-medium" class:text-green-600={set.completed} class:text-gray-600={!set.completed}>
              {setIndex + 1}
            </div>
            <div class="col-span-4">
              <input
                type="number"
                class="input-field w-full text-sm py-1"
                class:bg-green-50={set.completed}
                placeholder="0"
                bind:value={set.weight}
                min="0"
                max="9999"
                step="0.5"
              />
            </div>
            <div class="col-span-4">
              <input
                type="number"
                class="input-field w-full text-sm py-1"
                class:bg-green-50={set.completed}
                placeholder="0"
                bind:value={set.reps}
                min="0"
                max="999"
              />
            </div>
            <div class="col-span-2 flex items-center justify-center gap-1">
              <button
                type="button"
                class="w-7 h-7 rounded-full border-2 flex items-center justify-center transition-all duration-200 shadow-sm"
                class:bg-green-500={set.completed}
                class:border-green-500={set.completed}
                class:text-white={set.completed}
                class:scale-110={set.completed}
                class:border-gray-300={!set.completed}
                class:hover:border-green-400={!set.completed}
                class:hover:bg-green-50={!set.completed}
                on:click={() => toggleCompleted(setIndex)}
                title={set.completed ? '取消完成' : '標記完成'}
              >
                {#if set.completed}
                  <span class="text-sm font-bold">✓</span>
                {/if}
              </button>
              {#if exercise.sets.length > 1}
                <button
                  type="button"
                  class="w-6 h-6 rounded-full text-gray-400 hover:text-red-500 hover:bg-red-50 text-sm flex items-center justify-center transition-colors"
                  on:click={() => removeSet(setIndex)}
                  title="刪除此組"
                >
                  ×
                </button>
              {/if}
            </div>
          </div>
        {/each}
      </div>

      <button
        type="button"
        class="w-full mt-3 py-2 text-sm text-primary-600 hover:text-primary-800 hover:bg-primary-50 rounded-lg transition-colors border border-dashed border-primary-300"
        on:click={addSet}
      >
        + 新增一組
      </button>
    </div>
  </div>
</div>
